import {
  useEffect,
  useMemo,
  useCallback,
  memo,
  isValidElement,
  type ReactNode,
  type MouseEvent as ReactMouseEvent,
} from "react";
import Markdown, { Components, defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import Message from "../../../models/conversation/Message";
import { useNavigate } from "react-router-dom";
import { useDocumentReferenceStore } from "@/stores/document-reference.store";
import useLawDictionaryStore from "@/stores/law-dictionary-control.store";
import useLawTermToggleStore from "@/stores/lawtermtoggle.store";
import { useLawTermsContext } from "@/providers/law-terms/LawTermsContext";

// Import from our utility files
import { wrapTermsWithTooltip } from "./MarkdownUtils/LawTermProcessor";
import { wrapLemmatizedTermsWithTooltip } from "./MarkdownUtils/LemmatizedTermProcessor";
import {
  DelayedTooltip,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { nord } from "react-syntax-highlighter/dist/esm/styles/prism";
import SyntaxHighlighter from "react-syntax-highlighter";
import {
  showErrorToast,
  showInfoToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import {
  navigateToReference,
} from "@/utils/referenceNavigation";
import { NEW_TAB_MODIFIER_TOOLTIP } from "@/lib/navigation/appNavigation";
import { extractCitationIndex } from "@/lib/reference-citation-markdown";

type MarkdownComponentProps = {
  message: Message;
  isOverview?: boolean;
  componentsOverride?: Components;
  shouldMatchLawTerms?: boolean;
  exposeCopySource?: boolean;
};

const MARKDOWN_LINK_PATTERN = "\\[[^\\]\\n]+\\]\\([^)]+\\)";

const normalizeMarkdownLinkSpacing = (text: string): string => {
  return text
    .replace(
      new RegExp(`\\]\\(([^)\\s]+)\\)(?=${MARKDOWN_LINK_PATTERN})`, "g"),
      "]($1) "
    )
    .replace(
      new RegExp(`([.,;:!?])(?=${MARKDOWN_LINK_PATTERN})`, "g"),
      "$1 "
    )
    .replace(
      new RegExp(`(\\d)\\.(?=${MARKDOWN_LINK_PATTERN})`, "g"),
      "$1. "
    );
};

const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "BLOCKQUOTE",
  "PRE",
]);

const serializeNodeForClipboard = (node: Node, listDepth = 0): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? "";
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const element = node as HTMLElement;
  const tagName = element.tagName;

  if (tagName === "BR") {
    return "\n";
  }

  if (tagName === "LI") {
    const itemIndent = "  ".repeat(Math.max(0, listDepth - 1));
    const bulletPrefix = `${itemIndent}• `;
    const continuationPrefix = `${itemIndent}  `;
    const content = Array.from(element.childNodes)
      .map((childNode) => serializeNodeForClipboard(childNode, listDepth))
      .join("")
      .replace(/\u00A0/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim()
      .replace(/\n/g, `\n${continuationPrefix}`);

    return content ? `${bulletPrefix}${content}\n` : "";
  }

  if (tagName === "UL" || tagName === "OL") {
    const content = Array.from(element.childNodes)
      .map((childNode) => serializeNodeForClipboard(childNode, listDepth + 1))
      .join("");

    return `${content}\n`;
  }

  const content = Array.from(element.childNodes)
    .map((childNode) => serializeNodeForClipboard(childNode, listDepth))
    .join("");

  if (BLOCK_TAGS.has(tagName)) {
    return `${content}\n\n`;
  }

  return content;
};

const formatSelectedTextForClipboard = (selection: Selection): string | null => {
  if (selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }

  const fragment = selection.getRangeAt(0).cloneContents();
  const container = document.createElement("div");
  container.appendChild(fragment);

  const serialized = Array.from(container.childNodes)
    .map(serializeNodeForClipboard)
    .join("");

  return serialized
    .replace(/\u00A0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const formatSelectedHtmlForClipboard = (selection: Selection): string | null => {
  if (selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }

  const fragment = selection.getRangeAt(0).cloneContents();
  const container = document.createElement("div");
  container.appendChild(fragment);

  // Rich paste targets do not carry over Tailwind classes, so add minimal inline
  // spacing styles for block elements to preserve readable paragraph/list gaps.
  container
    .querySelectorAll("p, h1, h2, h3, h4, h5, h6, blockquote, pre")
    .forEach((element) => {
      (element as HTMLElement).style.margin = "0 0 1em 0";
    });

  container.querySelectorAll("ul, ol").forEach((element) => {
    const htmlElement = element as HTMLElement;
    htmlElement.style.margin = "0 0 1em 1.25em";
    htmlElement.style.padding = "0";
  });

  container.querySelectorAll("li").forEach((element) => {
    (element as HTMLElement).style.margin = "0 0 0.35em 0";
  });

  const html = container.innerHTML.trim();

  return html.length > 0 ? html : null;
};

function markdownLinkChildrenToPlainText(children: ReactNode): string {
  if (children === null || children === undefined) return "";

  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(markdownLinkChildrenToPlainText).join("");
  }

  if (isValidElement(children)) {
    const record = children.props as { children?: ReactNode };
    return markdownLinkChildrenToPlainText(record.children);
  }

  return "";
}

// Memoize the entire component to avoid rerendering when parent rerenders
const MarkdownComponent = memo(
  ({
    message,
    isOverview,
    componentsOverride,
    shouldMatchLawTerms = true,
    exposeCopySource = false,
  }: MarkdownComponentProps) => {
    const navigate = useNavigate();
    const {
      setSelectedBoundingBoxes,
      setSelectedDocumentReference,
      setDocumentRefIdsOrdered,
      setDocumentReferences,
    } = useDocumentReferenceStore();
    const { setIsDialogOpen, setSelectedTerm, setSelectedLetter } =
      useLawDictionaryStore();
    const { allLawTerms } = useLawTermsContext();
    const { resetWorkspaceDocumentVersionStore } =
      useWorkspaceDocumentVersionStore();
    const { toggleLawTermShow } = useLawTermToggleStore();

    // Process text with law terms - moved to useMemo for optimization
    const processedText = useMemo(() => {
      if (!shouldMatchLawTerms) {
        return message.text;
      }

      let text = message.text.replace(/\n\n((?:[\d-]+\.|-)\s+)/g, "\n$1");
      text = text.replace(/(\d+\.\s+)\n/g, "$1");

      if (!message.human) {
        if (toggleLawTermShow) {
          text = wrapTermsWithTooltip(text, allLawTerms || []);
        }

        if (toggleLawTermShow && message.lemmatizedText && message.isClosed) {
          text = wrapLemmatizedTermsWithTooltip(
            text,
            allLawTerms || [],
            message.lemmatizedText
          );
        }
      }

      if (message.human) {
        return text;
      }

      return normalizeMarkdownLinkSpacing(text);
    }, [
      shouldMatchLawTerms,
      message.text,
      message.human,
      message.lemmatizedText,
      message.isClosed,
      toggleLawTermShow,
      allLawTerms,
    ]);

    // Memoize handler functions
    const handleTermClick = useCallback(
      (term: string) => {
        const termfound = allLawTerms?.filter(
          (item) => item.term.toLowerCase() === term.toLowerCase()
        );

        if (!termfound || termfound.length === 0) {
          logger.warn("Clicked law term is not in the loaded law terms", {
            operation: "openLawTerm",
            lawTermCount: allLawTerms?.length ?? 0,
          });
          showErrorToast("Term ikke fundet");
          return;
        }

        setSelectedTerm(termfound[0]);
        setIsDialogOpen(true);
        setSelectedLetter(term[0].toUpperCase());
      },
      [allLawTerms, setSelectedTerm, setIsDialogOpen, setSelectedLetter]
    );

    useEffect(() => {
      if (!message?.references?.length || !message?.text) return;

      const sortedReferences = [...message.references].sort((a, b) => {
        const regexA = new RegExp(`\\[.*?\\]\\(${a.referenceId}\\)`);
        const regexB = new RegExp(`\\[.*?\\]\\(${b.referenceId}\\)`);

        const matchA = message.text.match(regexA);
        const matchB = message.text.match(regexB);

        if (!matchA && !matchB) return 0;
        if (!matchA) return 1;
        if (!matchB) return -1;

        return (
          message.text.indexOf(matchA[0]) - message.text.indexOf(matchB[0])
        );
      });

      setDocumentRefIdsOrdered(
        sortedReferences.map(
          (reference) => reference.referenceId?.toString() ?? ""
        )
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message]);

    const goToReference = useCallback(
      (
        props: { href?: string },
        m: Message,
        event: ReactMouseEvent<HTMLElement>
      ) => {
        if (!props.href) {
          logger.warn("Chat citation link has no reference id", {
            operation: "openChatReference",
            conversationId: m.conversationId,
            messageId: m.id,
          });
          showErrorToast("Reference blev ikke fundet");
          return;
        }

        if (props.href.startsWith("http://") || props.href.startsWith("https://")) {
          return;
        }

        const reference = m.references.find(
          (reference) => reference.referenceId === Number(props.href)
        );

        if (!reference) {
          logger.warn("Chat citation points to a missing reference", {
            operation: "openChatReference",
            conversationId: m.conversationId,
            messageId: m.id,
            referenceId: props.href,
            referenceCount: m.references.length,
          });
          showErrorToast("Reference blev ikke fundet");
          return;
        }

        navigateToReference({
          reference,
          references: m.references,
          navigate,
          event,
          store: {
            setSelectedBoundingBoxes,
            setSelectedDocumentReference,
            setDocumentReferences,
            resetWorkspaceDocumentVersionStore,
          },
          onError: (message) => showErrorToast(message),
          onDocumentIndexTypeDocument: () =>
            showInfoToast(
              "Document index type burde ikke findes i Web appen. Hvis du ser dette, så kontakt support.",
              "Kontakt support"
            ),
          conversationId: m.conversationId,
        });
      },
      [
        navigate,
        setSelectedBoundingBoxes,
        setSelectedDocumentReference,
        setDocumentReferences,
        resetWorkspaceDocumentVersionStore,
      ]
    );

    const handleMarkdownCopy = useCallback(
      (event: React.ClipboardEvent<HTMLDivElement>) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
          return;
        }

        const range = selection.getRangeAt(0);
        if (!event.currentTarget.contains(range.commonAncestorContainer)) {
          return;
        }

        const formattedText = formatSelectedTextForClipboard(selection);
        const formattedHtml = formatSelectedHtmlForClipboard(selection);
        if (!formattedText) {
          return;
        }

        event.preventDefault();
        event.clipboardData.setData("text/plain", formattedText);
        if (formattedHtml) {
          event.clipboardData.setData("text/html", formattedHtml);
        }
      },
      []
    );

    const baseComponents = useMemo(
      () => ({
      h1: ({ ...props }) => (
        <h1 className="text-2xl font-bold mb-2 mt-4 first:mt-0" {...props} />
      ),
      h2: ({ ...props }) => (
        <h2 className="text-xl font-bold mb-2 mt-4 first:mt-0" {...props} />
      ),
      h3: ({ ...props }) => (
        <h3 className="text-lg font-bold mb-2 mt-4 first:mt-0" {...props} />
      ),
      h4: ({ ...props }) => (
        <h4 className="text-base font-bold mb-2 mt-4 first:mt-0" {...props} />
      ),
      h5: ({ ...props }) => (
        <h5 className="text-sm font-bold mb-2 mt-4 first:mt-0" {...props} />
      ),
      h6: ({ ...props }) => (
        <h6 className="text-xs font-bold mb-2 mt-4 first:mt-0" {...props} />
      ),
      img: ({ ...props }) => (
        <img className="max-w-full h-auto bg-transparent" {...props} />
      ),
      ul: ({ ...props }) => (
        <ul
          className="list-disc list-outside block p-0 pl-5 m-0 leading-none"
          {...props}
        />
      ),
      ol: ({ ...props }) => (
        <ol
          className="list-decimal list-outside block p-0 pl-5 m-0 leading-none"
          {...props}
        />
      ),
      li: ({ ...props }) => <li className="my-4 leading-5 p-0" {...props} />,
      p: ({ ...props }) => (
        <p className="mt-4 mb-4 first:mt-0 last:mb-0" {...props} />
      ),

      table: ({ ...props }) => (
        <div className="overflow-x-auto mb-4">
          <table className="w-full border-collapse" {...props} />
        </div>
      ),
      thead: ({ ...props }) => <thead className="bg-accent" {...props} />,
      tbody: ({ ...props }) => <tbody {...props} />,
      tr: ({ ...props }) => (
        <tr
          className="border-b border-border dark:border-muted-foreground even:bg-muted/5 "
          {...props}
        />
      ),
      th: ({ ...props }) => (
        <th
          className="p-2 text-left font-semibold border-r dark:border-muted-foreground border-border bg-muted/10 first:rounded-tl last:rounded-tr last:border-r-0"
          {...props}
        />
      ),
      td: ({ ...props }) => (
        <td
          className="p-2 border-r border-border dark:border-muted-foreground last:border-r-0 "
          {...props}
        />
      ),
      code({ ...props }) {
        const { children, className, ...rest } = props;
        const match = /language-(\w+)/.exec(className || "");
        return match ? (
          <SyntaxHighlighter
            PreTag="div"
            children={String(children).replace(/\n$/, "")}
            language={match[1]}
            style={nord}
          />
        ) : (
          <code
            {...rest}
            className={cn(className, "px-[0.3rem] py-[0.2rem] rounded text-sm")}
          >
            {children}
          </code>
        );
      },
      a: ({ ...props }) => {
        const isTooltipLink = props.href === "##";
        const isExternalLink =
          typeof props.href === "string" &&
          (props.href.startsWith("http://") ||
            props.href.startsWith("https://"));

        const formatTooltipContent = (definitionText: string) => {
          // Remove any originalTerm part if present
          definitionText = definitionText.split("|")[0] || definitionText;

          // Extract any terms referenced with "{TermName}" pattern
          const referencedTerms = Array.from(
            definitionText.matchAll(/\{([^}]+)\}/g),
            (m) => m[1]
          );

          // Replace curly braces with single quotes for display
          const formattedText = definitionText.replace(/\{([^}]+)\}/g, "'$1'");

          // For each referenced term, look up its definition
          const additionalDefinitions = referencedTerms
            .map((termName) => {
              const termFound = allLawTerms?.find(
                (item) => item.term.toLowerCase() === termName.toLowerCase()
              );
              if (termFound) {
                return `<br /> <br /> <strong>${termFound.term}</strong><br/>${termFound.definition}`;
              }
              return "";
            })
            .join("");

          return formattedText + additionalDefinitions;
        };

        if (isTooltipLink) {
          // Extract definition and format for copying
          const definitionText = props.title ? props.title.split("|")[0] : "";
          const formattedDefinition = formatTooltipContent(definitionText);
          // Strip HTML tags for plain text copy
          const plainTextDefinition = formattedDefinition
            .replace(/<br\s*\/?>/gi, " ")
            .replace(/<[^>]*>/g, "")
            .trim();

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline text-lawterm-color font-bold cursor-pointer"
                    onClick={() => {
                      if (props.title && typeof props.title === "string") {
                        // Extract original term if present in title (format: "definition|originalTerm")
                        const parts = props.title.split("|");
                        const originalTerm =
                          parts.length > 1
                            ? parts[1]
                            : props.children && typeof props.children === "string"
                              ? props.children
                              : "";

                        if (!isOverview) {
                          handleTermClick(originalTerm);
                        }
                      } else if (
                        props.children &&
                        typeof props.children === "string"
                      ) {
                        if (!isOverview) {
                          handleTermClick(props.children);
                        }
                      } else {
                        logger.warn("Clicked law term link has no text", {
                          operation: "openLawTerm",
                        });
                        showErrorToast("Term ikke fundet");
                      }
                    }}
                  >
                    {props.children}
                    {plainTextDefinition.trim() && (
                      <span className="copy-only" aria-hidden="true">
                        {" "}
                        (Definition: {plainTextDefinition})
                      </span>
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p
                    className="max-w-[300px]"
                    dangerouslySetInnerHTML={{
                      __html: `<strong>Definition:</strong> ${formattedDefinition}`,
                    }}
                  />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }

        // External links - open in new tab directly
        if (isExternalLink) {
          return (
            <a
              target="_blank"
              rel="noreferrer"
              href={props.href}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: "normal",
              }}
              className="text-link"
            >
              {props.children}
            </a>
          );
        }

        const hrefPlain = (props.href ?? "").trim();
        const isNumericInternalCitation = /^\d+$/.test(hrefPlain);

        if (isNumericInternalCitation && !message.human) {
          const citationLabel = markdownLinkChildrenToPlainText(
            props.children
          );
          const hasReference = message.references.some(
            (reference) => reference.referenceId === Number(hrefPlain)
          );
          if (message.isClosed && !hasReference) {
            // chat-api dropped this citation (no openable document behind it),
            // so show the model's label as plain text instead of a dead [n].
            return (
              <span className="text-muted-foreground">{citationLabel}</span>
            );
          }
          const indexToken = extractCitationIndex(citationLabel);
          const citationReady = message.isClosed;
          const MAX_CITATION_ARIA_LABEL_CHARS = 240;
          const trimmedCitationLabel = citationLabel.trim();
          const citationLabelForAria =
            trimmedCitationLabel.length === 0
              ? ""
              : trimmedCitationLabel.length > MAX_CITATION_ARIA_LABEL_CHARS
                ? `${trimmedCitationLabel.slice(0, MAX_CITATION_ARIA_LABEL_CHARS)}…`
                : trimmedCitationLabel;
          const citationAriaLabel =
            citationLabelForAria.length > 0
              ? `Åbn henvisning ${indexToken}: ${citationLabelForAria}`
              : `Åbn henvisning ${indexToken}`;

          const citationFace = (
            <sup className="text-[0.75em] leading-none font-semibold">
              [{indexToken}]
            </sup>
          );

          const sharedButtonClasses =
            "text-link inline align-baseline border-0 bg-transparent p-0 font-inherit no-underline decoration-transparent";

          return (
            <DelayedTooltip delayDuration={400}>
              <TooltipTrigger asChild>
                {citationReady ? (
                  <button
                    type="button"
                    aria-label={citationAriaLabel}
                    onClick={(e) => goToReference(props, message, e)}
                    className={cn(
                      sharedButtonClasses,
                      "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    )}
                  >
                    {citationFace}
                  </button>
                ) : (
                  <span className="inline cursor-not-allowed align-baseline opacity-60">
                    <button
                      type="button"
                      disabled
                      aria-label={citationAriaLabel}
                      tabIndex={-1}
                      className={cn(
                        sharedButtonClasses,
                        "pointer-events-none cursor-not-allowed opacity-60"
                      )}
                    >
                      {citationFace}
                    </button>
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p className="text-left text-sm whitespace-pre-wrap">
                  {citationLabel}
                </p>
                <p className="text-muted-foreground mt-2 text-xs">
                  {NEW_TAB_MODIFIER_TOOLTIP}
                </p>
              </TooltipContent>
            </DelayedTooltip>
          );
        }

        if (hrefPlain) {
          return (
            <a
              href={hrefPlain}
              title={props.title}
              className={cn(
                "text-link font-normal underline",
                props.className
              )}
            >
              {props.children}
            </a>
          );
        }

        return <span className="text-muted-foreground">{props.children}</span>;
      },
    }),
    [
      allLawTerms,
      goToReference,
      handleTermClick,
      isOverview,
      message,
    ]
  );

    const mergedComponents = useMemo(
      () => ({
        ...baseComponents,
        ...componentsOverride,
      }),
      [baseComponents, componentsOverride]
    );

    // Render the markdown component with memoized values
    const renderedMarkdown = useMemo(
      () => (
        <div
          onCopy={handleMarkdownCopy}
          data-markdown-copy-source={exposeCopySource ? message.id : undefined}
        >
          <Markdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            urlTransform={defaultUrlTransform}
            components={mergedComponents}
          >
            {processedText}
          </Markdown>
        </div>
      ),
      [
        processedText,
        mergedComponents,
        handleMarkdownCopy,
        exposeCopySource,
        message.id,
      ]
    );

    return <>{renderedMarkdown}</>;
  }
);

export default MarkdownComponent;
